import { useCurrentUser, useIsOrgAdmin } from '@/features/identity';
import type {
  WorkspaceAggregate,
  WorkspaceRole,
} from '@/lib/api/generated/model';

import { useWorkspace } from './useWorkspaces';

/**
 * What this person may do in a workspace, in one place (spec 188).
 *
 * The service decides for real (spec 151): every write a person may not make is
 * refused there. This decides only what the UI offers, so it reads what the
 * workspace read already carries — `access`, which the service fills in for the
 * caller — and costs no request of its own.
 */
export interface WorkspaceStanding {
  /** Whether the workspace read has arrived; everything below is a default until then. */
  known: boolean;
  /** Whether access is checked at all. Off, everyone may do everything, as before. */
  enforced: boolean;
  role: WorkspaceRole | null;
  /**
   * An owner of this workspace: a member with the owner role, or — for a workspace
   * from before membership was recorded — its creator (spec 151 FR-009).
   */
  isOwner: boolean;
  /** May work in it: tasks, runs, comments, workflows, files, labels, triggers. */
  canWork: boolean;
  /** May change its people, settings, connections and secrets. */
  canManage: boolean;
  /** Sees an Organization workspace they are not in: everything, changing nothing. */
  readOnly: boolean;
  /** An org admin in a workspace they have not joined (spec 155 FR-002). */
  asOrgAdmin: boolean;
  /** May read its spend figures: an owner, or an org admin (spec 075). */
  mayReadUsage: boolean;
}

/** The standing from a workspace read and who is reading; pure, for tests. */
export function standingOf(
  workspace: WorkspaceAggregate | null | undefined,
  currentUserId: string | undefined,
  isOrgAdmin: boolean,
): WorkspaceStanding {
  const access = workspace?.access ?? null;
  const role = access?.role ?? null;
  const enforced = access?.enforced ?? false;
  // Until the read arrives nothing is offered on a guess — the same default the
  // sidebar always had for Usage — but nothing is taken away either: pages wait for
  // the read themselves, and a control that vanishes on a slow read and comes back
  // is worse than one that waits with the page.
  const canWork = access?.can_work ?? true;
  const canManage = access?.can_manage ?? true;
  const isOwner =
    role === 'owner' ||
    (role === null &&
      currentUserId !== undefined &&
      workspace?.created_by != null &&
      workspace.created_by.id === currentUserId);
  return {
    known: workspace != null,
    enforced,
    role,
    isOwner,
    canWork,
    canManage,
    readOnly: enforced && !canWork,
    asOrgAdmin: access?.as_org_admin ?? false,
    mayReadUsage: isOwner || isOrgAdmin,
  };
}

export function useWorkspaceStanding(workspaceId: string): WorkspaceStanding {
  const { data: workspace } = useWorkspace(workspaceId);
  const { data: currentUser } = useCurrentUser();
  const isOrgAdmin = useIsOrgAdmin();
  return standingOf(workspace, currentUser?.id, isOrgAdmin);
}
